import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isIndexableRoute, normalizePath, routeDescription, CANONICAL_HOST } from '@/routes/manifest';

/**
 * Keeps the robots, canonical and description tags in step with the current route.
 *
 * One component rather than a call in every page, because the answer comes
 * from routes/manifest.ts and not from the page. Adding a route therefore
 * cannot forget this: an undeclared or non-indexable path gets `noindex`.
 *
 * This is the second line of defence. robots.txt already blocks crawling of the
 * private routes, and the CloudFront function already 404s unknown ones. The tag
 * covers the case robots.txt cannot: a URL someone links to directly, which
 * Google may index from the link alone without ever crawling it.
 *
 * It renders nothing. It edits the document head directly, which is why there
 * is no head-management library here for four tags.
 *
 * index.html carries the static defaults that anything reading the shell before
 * React boots will see: crawlers, link previews, social cards. Those defaults
 * describe the indexable pages, so they ship `index, follow`. This component
 * overrides them per route once the app mounts. The explanation lives here and
 * not in index.html, because an HTML comment is served to every visitor.
 */
export function RouteMeta() {
    const { pathname } = useLocation();

    useEffect(() => {
        // The canonical URL must use the same spelling the manifest matched on.
        // /login/ and /login are one page, and both must name /login.
        const path = normalizePath(pathname);
        const indexable = isIndexableRoute(path);

        setMetaTag('robots', indexable ? 'index, follow' : 'noindex, nofollow');

        // A canonical URL only makes sense for a page we want in the index.
        setCanonical(indexable ? `https://${CANONICAL_HOST}${path}` : null);

        setDescription(routeDescription(path));
    }, [pathname]);

    return null;
}

function setMetaTag(name: string, content: string): void {
    let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

    if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
    }

    tag.content = content;
}

/**
 * Writes the route's description, or puts back the one index.html shipped with.
 *
 * The first write keeps the index.html text in a data attribute, so index.html
 * stays the only copy of the default and no page inherits the previous page's text.
 */
function setDescription(description: string | null): void {
    const tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');

    if (tag && tag.dataset.default === undefined) {
        tag.dataset.default = tag.content;
    }

    const content = description ?? tag?.dataset.default;

    if (content !== undefined) {
        setMetaTag('description', content);
    }
}

function setCanonical(href: string | null): void {
    const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (href === null) {
        existing?.remove();

        return;
    }

    const link = existing ?? document.createElement('link');
    link.rel = 'canonical';
    link.href = href;

    if (!existing) {
        document.head.appendChild(link);
    }
}
